import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  EmptyState,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  SearchInput,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Toaster,
  Tag,
  cn,
} from "@jem-hr/jem-ui";
import { Check, ChevronDown, FileSearch, MapPin, Users as UsersIcon } from "lucide-react";
import { ReportWizardBody, type ReportScope } from "../components/ReportWizard";
import { CustomReports, type CustomReportsProps } from "../components/CustomReports";

/* ================================================================== */
/*  Mock data — sites and rosters                                      */
/*  Mirrors the shapes used in TimeAttendance.tsx so the two pages     */
/*  feel native to the same product.                                   */
/* ================================================================== */

interface MockSite {
  id: string;
  name: string;
  region: string;
  area: string;
}

interface MockRoster {
  id: string;
  code: string;
  name: string;
  parentSite: string;
}

const MOCK_SITES: MockSite[] = [
  { id: "s-retail-floor", name: "Retail Floor", region: "Western Cape", area: "Cape Town CBD" },
  { id: "s-stockroom", name: "Stockroom", region: "Western Cape", area: "Cape Town CBD" },
  { id: "s-sandton-city", name: "Sandton City", region: "Gauteng", area: "Gauteng Central" },
  { id: "s-rosebank-mall", name: "Rosebank Mall", region: "Gauteng", area: "Gauteng Central" },
  { id: "s-carlton-centre", name: "Carlton Centre", region: "Gauteng", area: "JHB CBD" },
  { id: "s-gateway-theatre", name: "Gateway Theatre", region: "KwaZulu-Natal", area: "Durban Central" },
  { id: "s-baywest-mall", name: "Baywest Mall", region: "Eastern Cape", area: "Port Elizabeth" },
  { id: "s-or-tambo-logistics-hub", name: "OR Tambo Logistics Hub", region: "Gauteng", area: "Gauteng East" },
];

const MOCK_ROSTERS: MockRoster[] = [
  { id: "r-rd016d", code: "RD016D", name: "SASOL SHIFT SUPERVISORS", parentSite: "JHB CBD" },
  { id: "r-rd016e", code: "RD016E", name: "SASOL SHUTTLE DRIVERS", parentSite: "JHB CBD" },
  { id: "r-rd016h", code: "RD016H", name: "SASOL TACTICAL RESPONSE UNIT", parentSite: "JHB CBD" },
  { id: "r-gm011g", code: "GM011G", name: "TERACO ISANDO JB 3", parentSite: "Gauteng East" },
  { id: "r-ct004a", code: "CT004A", name: "V&A WATERFRONT SECURITY", parentSite: "Cape Town CBD" },
];

/* ================================================================== */
/*  SearchableSelect — popover-based combobox                          */
/* ================================================================== */

interface SearchableSelectOption {
  value: string;
  label: string;
  sublabel?: string;
  /** Used for search matching. Defaults to label + sublabel if not set. */
  keywords?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder: string;
  emptyMessage: string;
  className?: string;
}

function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  emptyMessage,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listboxId = useId();
  const optionIdBase = useId();
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = (o.keywords ?? `${o.label} ${o.sublabel ?? ""}`).toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  // Reset highlighted index whenever the filtered set changes or the popover opens.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, open]);

  // Keep the highlighted option visible in the scroll container.
  useEffect(() => {
    if (!open) return;
    const el = optionRefs.current[highlightedIndex];
    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex, open]);

  const commitSelection = (index: number) => {
    const opt = filtered[index];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (filtered.length === 0) {
      if (e.key === "Escape") setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlightedIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlightedIndex(filtered.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      commitSelection(highlightedIndex);
    }
  };

  const activeOptionId =
    filtered.length > 0 && open ? `${optionIdBase}-${highlightedIndex}` : undefined;

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          className={cn(
            "flex items-center justify-between w-full h-[40px] rounded-md border border-greyscale-border bg-white px-sm text-sm transition-colors",
            "hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-brand-primary/40",
            !selected && "text-greyscale-text-caption",
            className,
          )}
        >
          <span className="truncate text-left">
            {selected ? (
              <>
                <span className="text-greyscale-text-title">{selected.label}</span>
                {selected.sublabel && (
                  <span className="text-greyscale-text-caption"> — {selected.sublabel}</span>
                )}
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronDown size={16} className="text-greyscale-text-caption shrink-0 ml-xs" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
        onKeyDown={handleKeyDown}
      >
        <div className="p-xs border-b border-greyscale-border">
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery("")}
            placeholder="Search…"
            aria-controls={listboxId}
            aria-activedescendant={activeOptionId}
            autoFocus
          />
        </div>
        <div
          id={listboxId}
          role="listbox"
          className="max-h-[260px] overflow-y-auto py-xs"
        >
          {filtered.length === 0 ? (
            <p className="px-md py-sm text-sm text-greyscale-text-caption">
              {emptyMessage}
            </p>
          ) : (
            filtered.map((opt, index) => {
              const isSelected = opt.value === value;
              const isHighlighted = index === highlightedIndex;
              return (
                <button
                  key={opt.value}
                  id={`${optionIdBase}-${index}`}
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={-1}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => commitSelection(index)}
                  className={cn(
                    "w-full flex items-center gap-xs px-md py-xs text-left text-sm transition-colors",
                    isHighlighted && "bg-neutral-100",
                    isSelected && "bg-secondary-pink-50 text-greyscale-text-title",
                    !isHighlighted && !isSelected && "text-greyscale-text-body",
                  )}
                >
                  <Check
                    size={14}
                    className={cn(
                      "shrink-0 text-primary-brand-primary",
                      isSelected ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex-1 min-w-0 truncate">
                    <span className="font-semibold">{opt.label}</span>
                    {opt.sublabel && (
                      <span className="text-greyscale-text-caption font-regular"> — {opt.sublabel}</span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ================================================================== */
/*  Scope Picker                                                       */
/* ================================================================== */

type ScopeKind = "site" | "roster";

function ScopePicker({
  scopeKind,
  onScopeKindChange,
  selectedId,
  onSelectedIdChange,
}: {
  scopeKind: ScopeKind;
  onScopeKindChange: (v: ScopeKind) => void;
  selectedId: string | null;
  onSelectedIdChange: (v: string | null) => void;
}) {
  return (
    <div className="rounded-lg border border-greyscale-border bg-white p-md space-y-md">
      <div>
        <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
          What should this report cover?
        </Label>
        <RadioGroup
          value={scopeKind}
          onValueChange={(v) => {
            onScopeKindChange(v as ScopeKind);
            onSelectedIdChange(null);
          }}
          className="flex flex-wrap items-center gap-md sm:gap-lg"
        >
          <div className="flex items-center gap-xs">
            <RadioGroupItem value="site" id="scope-site" />
            <Label htmlFor="scope-site" className="text-sm flex items-center gap-xxs">
              <MapPin size={14} className="text-greyscale-text-caption" />
              A site
            </Label>
          </div>
          <div className="flex items-center gap-xs">
            <RadioGroupItem value="roster" id="scope-roster" />
            <Label htmlFor="scope-roster" className="text-sm flex items-center gap-xxs">
              <UsersIcon size={14} className="text-greyscale-text-caption" />
              A roster
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div>
        <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
          {scopeKind === "site" ? "Pick a site" : "Pick a roster"}
        </Label>
        <SearchableSelect
          className="w-full sm:max-w-[420px]"
          value={selectedId}
          onChange={onSelectedIdChange}
          placeholder={scopeKind === "site" ? "Search sites…" : "Search rosters…"}
          emptyMessage={scopeKind === "site" ? "No sites match your search." : "No rosters match your search."}
          options={
            scopeKind === "site"
              ? MOCK_SITES.map((s) => ({
                  value: s.id,
                  label: s.name,
                  sublabel: s.region,
                  keywords: `${s.name} ${s.region} ${s.area}`,
                }))
              : MOCK_ROSTERS.map((r) => ({
                  value: r.id,
                  label: `${r.parentSite} (${r.code})`,
                  sublabel: r.name,
                  keywords: `${r.parentSite} ${r.code} ${r.name}`,
                }))
          }
        />
      </div>
    </div>
  );
}

/* ================================================================== */
/*  General Reports tab — the original ENG-3031 wizard, unchanged       */
/* ================================================================== */

function GeneralReports() {
  const [scopeKind, setScopeKind] = useState<ScopeKind>("site");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Empty-state toggle — no sites/rosters onboarded yet. For demo purposes
  // we keep it off but leave the pathway so QA can see the state.
  const hasScopeOptions = MOCK_SITES.length > 0 || MOCK_ROSTERS.length > 0;

  const scope: ReportScope | null = useMemo(() => {
    if (!selectedId) return null;
    if (scopeKind === "site") {
      const s = MOCK_SITES.find((x) => x.id === selectedId);
      if (!s) return null;
      return { kind: "site", id: s.id, label: s.name, region: s.region };
    }
    const r = MOCK_ROSTERS.find((x) => x.id === selectedId);
    if (!r) return null;
    return {
      kind: "roster",
      id: r.id,
      label: `${r.parentSite} (${r.code}): ${r.name}`,
      parentSite: r.parentSite,
    };
  }, [scopeKind, selectedId]);

  if (!hasScopeOptions) {
    return (
      <div className="mt-lg">
        <EmptyState
          icon={<FileSearch size={32} />}
          title="No sites or rosters yet"
          description="Add at least one site or roster in Time & Attendance before generating reports."
        />
      </div>
    );
  }

  return (
    <div className="mt-md grid gap-md grid-cols-1 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
      {/* Left column: scope picker */}
      <div className="space-y-md">
        <ScopePicker
          scopeKind={scopeKind}
          onScopeKindChange={setScopeKind}
          selectedId={selectedId}
          onSelectedIdChange={setSelectedId}
        />

        {/* Quick links to contextual entry points */}
        <div className="rounded-lg border border-greyscale-border bg-neutral-50 p-md">
          <p className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption mb-xs">
            Tip
          </p>
          <p className="text-sm text-greyscale-text-body">
            You can also click <span className="font-semibold text-greyscale-text-title">Download Report</span> from any site or roster view in <span className="font-semibold">Time &amp; Attendance</span> to open this wizard pre-scoped.
          </p>
        </div>
      </div>

      {/* Right column: wizard body or empty scope hint */}
      <div>
        {scope ? (
          <div className="rounded-lg border border-greyscale-border bg-white p-md sm:p-lg">
            <ReportWizardBody scope={scope} />
          </div>
        ) : (
          <div className="hidden lg:flex rounded-lg border border-dashed border-greyscale-border bg-neutral-50 p-md sm:p-xl items-center justify-center min-h-[320px]">
            <div className="text-center max-w-[360px] w-full">
              <div className="inline-flex items-center justify-center rounded-full bg-secondary-pink-100 text-secondary-pink-900 h-12 w-12 mb-sm">
                <FileSearch size={22} />
              </div>
              <p className="text-sm font-semibold text-greyscale-text-title">
                Pick a {scopeKind === "site" ? "site" : "roster"} to continue
              </p>
              <p className="text-xs text-greyscale-text-caption mt-xxxs">
                After you pick a scope, choose the report type and set the filters.
              </p>
              <div className="flex items-center justify-center flex-wrap gap-xs mt-sm">
                <Tag variant="drafted" size="sm">Planned vs Actuals</Tag>
                <Tag variant="drafted" size="sm">Timesheet</Tag>
                <Tag variant="drafted" size="sm">Posting Sheet</Tag>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Main Reports page                                                  */
/*                                                                    */
/*  ENG-2965 restructure: two tabs                                    */
/*    - General Reports — existing ENG-3031 wizard (unchanged)        */
/*    - Custom Reports  — Roster Agent UI (this ticket)               */
/*                                                                    */
/*  ?customReportsVariant=default|resume|agent_unavailable            */
/*    |first_ever_employer|report_chat drives Custom Reports states   */
/*    for QA. ?tab=custom forces the Custom Reports tab on load.      */
/* ================================================================== */

export function Reports() {
  const params =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  const defaultTab = params.get("tab") === "custom" ? "custom" : "general";
  const customVariant: CustomReportsProps["variant"] =
    (params.get("customReportsVariant") as CustomReportsProps["variant"]) ?? "default";

  return (
    <div className={cn("pb-xl")}>
      {/* Page header — matches TimeAttendance pattern */}
      <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
        Reports
      </h1>
      <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
        Generate report exports or import a roster from an Excel, CSV, or PDF file.
      </p>

      <Tabs defaultValue={defaultTab} className="mt-md">
        <TabsList variant="line" className="w-full justify-start">
          <TabsTrigger value="general" variant="line">
            General Reports
          </TabsTrigger>
          <TabsTrigger value="custom" variant="line">
            Custom Reports
          </TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <GeneralReports />
        </TabsContent>
        <TabsContent value="custom">
          <div className="mt-md">
            <CustomReports variant={customVariant} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Toast portal — required for download-complete confirmation */}
      <Toaster position="bottom-right" />
    </div>
  );
}
